import React from 'react';
import styled from 'styled-components';

import {
  FindProjectModel,
  FindProjectResponse,
} from '../../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { AssetManager } from '../../../viewer/asset-manager';
import { InputHandler } from '../../../viewer/input-handler';
import { SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { ProjectGeodataManager } from '../../../viewer/project-geodata-manager';
import { SkyView } from '../../../viewer/skyview';
import state from '../../../viewer/state';
import { Tool } from '../../../viewer/tools/tool';
import { ClassicSceneType } from '../../../viewer/view-scenes';
import { SkyViewContextMenuProps } from '../../components/skyview/context-menu';
import { SkyViewLoader } from '../../components/skyview/skyview-loader';
import { SkyviewAssetPositionProps } from '../../components/skyview/tooltip/skyview-asset-position-tooltip';
import { SkyviewCanvasContainer } from '../../components/skyview-new/skyview-canvas-container';
import { useSubscribe } from '../../hooks/use-subscribe';
import { SkyviewFrameContent } from './skyview-frame-content';

type Props = {
  projectId: string;
  timelineId?: string;
  scene?: ClassicSceneType;
  token?: string;
  assetMenuVisible?: boolean;
};

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const tooltipTools: Set<string> = new Set([
  'measure',
  'select',
  'design',
  'asbuilt-geometry',
  'reference-point-transform',
  'volume-inspector',
]);

const contextMenuTools: Set<string> = new Set([
  'measure',
  'select',
  'design',
  'reference-point-transform',
  'transform',
  'volume-inspector',
  'intersection',
  'volume-calculator',
]);

type ToolWithUiHooks = Tool & {
  onTooltipChanged?: (callback: (tooltip?: SkyviewAssetPositionProps) => void) => void;
  onContextMenuChanged?: (callback: (menu?: SkyViewContextMenuProps) => void) => void;
};

type SkyviewWindow = Window & { skyview?: SkyView };

const SkyviewPage = ({ projectId, timelineId, scene, token, assetMenuVisible }: Props) => {
  const [skyView, setSkyView] = React.useState<SkyView>();
  const [loadState, setLoadState] = React.useState<LoadState>('loading');
  const [assetTooltip, setAssetTooltip] = React.useState<SkyviewAssetPositionProps>();
  const [contextMenu, setContextMenu] = React.useState<SkyViewContextMenuProps>();

  // Keep a mutable reference to ensure cleanup even if state changes during async work.
  const viewerRef = React.useRef<{ skyview?: SkyView }>({});
  const skyViewRef = React.useRef<SkyView>();
  const viewerContainerRef = React.useRef<HTMLDivElement>(null);
  const loadStartedRef = React.useRef(false);
  const viewer = viewerRef.current;

  const webglSupported = React.useMemo(() => !!window.WebGLRenderingContext, []);

  React.useEffect(() => {
    const setNativeApp = (window as unknown as { setIsNativeApp?: (state: boolean) => void })
      .setIsNativeApp;

    if (token) {
      setNativeApp?.(true);
      state.setValue('isNativeApp', true);
    } else {
      state.setValue('isNativeApp', false);
    }
  }, [token]);

  const registerToolCallbacks = React.useCallback(
    (tool?: ToolWithUiHooks) => {
      setAssetTooltip(undefined);
      setContextMenu(undefined);

      if (!tool) {
        return;
      }

      if (tooltipTools.has(tool.name)) {
        tool.onTooltipChanged?.((tooltip?: SkyviewAssetPositionProps) => setAssetTooltip(tooltip));
      } else {
        tool.onTooltipChanged?.(() => setAssetTooltip(undefined));
      }

      if (contextMenuTools.has(tool.name)) {
        tool.onContextMenuChanged?.((menu?: SkyViewContextMenuProps) => setContextMenu(menu));
      } else {
        tool.onContextMenuChanged?.(() => setContextMenu(undefined));
      }
    },
    [setAssetTooltip, setContextMenu],
  );

  const lastRegisteredToolRef = React.useRef<ToolWithUiHooks | null>(null);

  useSubscribe(SubscriptionTopic.SetTool, (tool) => {
    const toolInstance = tool as ToolWithUiHooks;
    lastRegisteredToolRef.current = toolInstance;
    registerToolCallbacks(toolInstance);
  });

  React.useEffect(() => {
    if (!skyView) {
      return;
    }

    const tool = InputHandler.instance.currentTool() as ToolWithUiHooks;
    // Only re-register when the tool instance changes to avoid setState loop when
    // tool callbacks (e.g. clearContextMenu on mouse move) trigger re-renders.
    if (lastRegisteredToolRef.current === tool) {
      return;
    }
    lastRegisteredToolRef.current = tool;
    registerToolCallbacks(tool);
  }, [registerToolCallbacks, skyView]);

  React.useEffect(() => {
    if (!webglSupported) {
      setLoadState('error');
      return;
    }

    if (loadStartedRef.current) {
      return () => undefined;
    }
    loadStartedRef.current = true;

    const cancelledRef = { current: false };

    const loadSkyview = async () => {
      setLoadState('loading');

      try {
        const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
        const response: FindProjectResponse = await projectService.getById({
          query: { projectId },
        });
        const project: FindProjectModel | undefined = response.data;

        if (!project) {
          throw new Error('Project payload is missing');
        }
        // Left before the project loaded: another SkyView may already be opening
        if (cancelledRef.current) {
          return;
        }

        ProjectGeodataManager.instance().load(project);

        AssetManager.instance.initialise(viewer);

        const skyviewInstance = new SkyView(project, {
          sceneManagerOptions: {
            defaultTimelineContainerId: timelineId,
            defaultSceneType: scene,
          },
          viewerElement: viewerContainerRef.current ?? undefined,
        });

        viewer.skyview = skyviewInstance;
        skyViewRef.current = skyviewInstance;

        await skyviewInstance.init();

        if (cancelledRef.current) {
          skyviewInstance.clearCustom();
          return;
        }

        (window as unknown as SkyviewWindow).skyview = skyviewInstance;
        setSkyView(skyviewInstance);
        setLoadState('ready');
      } catch (error) {
        if (!cancelledRef.current) {
          // eslint-disable-next-line no-console
          console.error('Failed to load SkyView', error);
          setLoadState('error');
        }
      }
    };

    void loadSkyview();

    return () => {
      setAssetTooltip(undefined);
      setContextMenu(undefined);

      // Also while the project is loading, before SkyView is created
      cancelledRef.current = true;
      const skyviewToClear = skyViewRef.current;

      if (skyviewToClear) {
        skyviewToClear.clearCustom();
        skyViewRef.current = undefined;
      }

      viewer.skyview = undefined;
      AssetManager.instance.clearCustom();
      loadStartedRef.current = false;
    };
  }, [projectId, scene, timelineId, webglSupported, viewer]);

  const isNativeApp = Boolean(state.getValue('isNativeApp'));

  if (!webglSupported) {
    return (
      <FullPageFallback id="webGlNotSupported">
        <span>SkyView kan inte visas då webbläsaren saknar stöd för WebGL.</span>
      </FullPageFallback>
    );
  }

  if (loadState === 'error') {
    return (
      <FullPageFallback>
        <span>Ett fel inträffade när SkyView skulle laddas. Försök igen.</span>
      </FullPageFallback>
    );
  }

  return (
    <ViewerShell data-testid="viewer" id="viewer" ref={viewerContainerRef}>
      <SkyviewCanvasContainer />

      {/* Show loader during initial fetch, before skyView is available. Once
          skyView exists, SkyviewFrameContent takes over all frame-driven rendering
          and manages its own loader for the scene-initialisation phase. */}
      {!skyView && <SkyViewLoader />}

      {/* SkyviewFrameContent is the sole subscriber to the viewer's animation
          loop. Isolating it here ensures SkyviewPage itself never re-renders at
          the viewer's render rate, keeping the asset menu tree stable. */}
      {skyView && (
        <SkyviewFrameContent
          assetMenuVisible={assetMenuVisible}
          assetTooltip={assetTooltip}
          contextMenu={contextMenu}
          isNativeApp={isNativeApp}
          skyView={skyView}
        />
      )}
    </ViewerShell>
  );
};

export { SkyviewPage };

const ViewerShell = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #0f0f10;
`;

const FullPageFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  text-align: center;
  padding: 1rem;
  box-sizing: border-box;
`;
